import { pointerIntersection } from '@dnd-kit/collision'
import { useDragDropMonitor, useDraggable, useDroppable } from '@dnd-kit/react'
import { useId } from 'react'

import { Include, Location, LocationScheduleSpeciality, PractitionerScheduleLocation, PractitionerScheduleStatus } from '~/storm'

export const dndItemTypes = {
    locationScheduleAllocation: 'LOCATION_SCHEDULE_ALLOCATION',
    practitionerScheduleLocation: 'PRACTITIONER_SCHEDULE_LOCATION',
    practitionerScheduleStatus: 'PRACTITIONER_SCHEDULE_STATUS',
} as const

type DndItemType = (typeof dndItemTypes)[keyof typeof dndItemTypes]

export type AllocationDragItem = { type: typeof dndItemTypes.locationScheduleAllocation; data: LocationScheduleSpeciality }
export type ScheduleLocationDragItem = {
    type: typeof dndItemTypes.practitionerScheduleLocation
    data: Include<PractitionerScheduleLocation, { location: true }>
}
type ScheduleStatusData = Include<PractitionerScheduleStatus, { definition: true }> & {
    locations: Location[]
}
export type ScheduleStatusDragItem = { type: typeof dndItemTypes.practitionerScheduleStatus; data: ScheduleStatusData }

export type TablesLayoutDragItem = AllocationDragItem | ScheduleLocationDragItem | ScheduleStatusDragItem
export type PractitionerActivityDragItem = ScheduleLocationDragItem | ScheduleStatusDragItem

export const allocationDragItem = (data: LocationScheduleSpeciality): AllocationDragItem => ({ type: dndItemTypes.locationScheduleAllocation, data })
export const scheduleLocationDragItem = (data: Include<PractitionerScheduleLocation, { location: true }>): ScheduleLocationDragItem => ({
    type: dndItemTypes.practitionerScheduleLocation,
    data,
})
export const scheduleStatusDragItem = (data: ScheduleStatusData): ScheduleStatusDragItem => ({
    type: dndItemTypes.practitionerScheduleStatus,
    data,
})

export const dragId = (item: TablesLayoutDragItem) => `${item.type}:${item.data.id}`

export const useOptionalDraggable = (dragItem?: TablesLayoutDragItem) => {
    const disabledId = useId()
    return useDraggable({
        id: dragItem ? dragId(dragItem) : disabledId,
        type: dragItem?.type,
        data: dragItem?.data ?? {},
        disabled: !dragItem,
    })
}

type ItemOfType<T extends DndItemType> = Extract<TablesLayoutDragItem, { type: T }>

const matchSource = <T extends DndItemType>(types: T[], source?: { type?: unknown; data?: unknown } | null): ItemOfType<T> | null => {
    if (!source || !types.includes(source.type as T)) return null
    return source as ItemOfType<T>
}

type Props<T extends DndItemType> = {
    id: string
    type: T | T[]
    canDrop: (item: ItemOfType<T>) => boolean
    onDrop: (item: ItemOfType<T>) => void
}

export const useTablesLayoutDrop = <T extends DndItemType>({ id, type, canDrop, onDrop }: Props<T>) => {
    const types = Array.isArray(type) ? type : [type]

    const accept = (source?: { type?: unknown; data?: unknown }) => {
        const item = matchSource(types, source)
        return item ? canDrop(item) : false
    }

    const { droppable, isDropTarget, ref: drop } = useDroppable({ id, accept, collisionDetector: pointerIntersection })

    useDragDropMonitor({
        onDragEnd: event => {
            if (event.operation.target?.id !== id) return
            const item = matchSource(types, event.operation.source)
            if (item && canDrop(item)) onDrop(item)
        },
    })

    const source = isDropTarget ? droppable.manager?.dragOperation.source : null

    return {
        drop,
        isActive: isDropTarget,
        item: matchSource(types, source),
    } as const
}
